import { Receipt, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import type { TableBill, TableOrderItem } from "@/hooks/usePublicMenu";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface TableBillSectionProps {
  bill: TableBill | null;
  orderItems: TableOrderItem[];
  tableNumber: string;
}

export function TableBillSection({ bill, orderItems, tableNumber }: TableBillSectionProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
      pending: { label: "Pendente", variant: "secondary" },
      preparing: { label: "Preparando", variant: "default" },
      ready: { label: "Pronto", variant: "default" },
      delivered: { label: "Entregue", variant: "outline" },
    };
    return statusMap[status] || { label: status, variant: "secondary" };
  };

  if (!bill) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Receipt className="h-5 w-5" />
            Mesa {tableNumber}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-4">
            Nenhum pedido aberto nesta mesa
          </p>
        </CardContent>
      </Card>
    );
  }

  const status = getStatusBadge(bill.status);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Receipt className="h-5 w-5" />
            Mesa {tableNumber}
          </CardTitle>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          Pedido #{bill.order_number} • {format(new Date(bill.created_at), "HH:mm", { locale: ptBR })}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {orderItems.length > 0 ? (
          <>
            <div className="space-y-2">
              {orderItems.map((item) => (
                <div key={item.id} className="flex justify-between text-sm">
                  <div className="flex-1">
                    <span className="font-medium">{item.quantity}x</span>{" "}
                    <span>{item.product_name}</span>
                    {item.notes && (
                      <p className="text-xs text-muted-foreground">{item.notes}</p>
                    )}
                  </div>
                  <span className="text-muted-foreground">{formatPrice(item.total_price)}</span>
                </div>
              ))}
            </div>

            <Separator />

            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatPrice(bill.subtotal)}</span>
              </div>
              {bill.discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Desconto</span>
                  <span>-{formatPrice(bill.discount)}</span>
                </div>
              )}
              {bill.additional_fees > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Taxa de serviço</span>
                  <span>{formatPrice(bill.additional_fees)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-lg pt-1">
                <span>Total</span>
                <span className="text-primary">{formatPrice(bill.total)}</span>
              </div>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-center py-2">
            Nenhum item no pedido ainda
          </p>
        )}
      </CardContent>
    </Card>
  );
}
